import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ParkingCardPendingTaiwanSyncRow } from '../../../../core/models/parking/parking-card-list.model';
import { MapperParkingCardListService } from '../../../../core/mappers/parking/mapper-parking-card-list.service';
import { ApiParkingCardListService } from '../../../../core/services/api/parking/api-parking-card-list.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';

@Component({
  selector: 'app-parking-cards-taiwan-pending',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent],
  templateUrl: './parking-cards-taiwan-pending.component.html',
  styleUrl: './parking-cards-taiwan-pending.component.css',
})
export class ParkingCardsTaiwanPendingComponent implements OnInit {
  static readonly MAX_SEND = 50;

  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiParkingCardListService);
  private readonly mapper = inject(MapperParkingCardListService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  rows: ParkingCardPendingTaiwanSyncRow[] = [];
  selectedIds = new Set<number>();

  searchText = '';
  currentPage = 1;
  pageSize = 20;
  pageSizeOptions = [20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  isLoading = false;
  isSending = false;
  isConfirmOpen = false;
  taiwanSite = false;
  hasTaiwanUrl = false;
  lastFetchedAt: Date | null = null;
  userId = 0;
  protected Math = Math;

  constructor() {
    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;
      untracked(() => {
        this.currentPage = 1;
        this.selectedIds.clear();
        this.loadTable();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  get maxSend(): number {
    return ParkingCardsTaiwanPendingComponent.MAX_SEND;
  }

  get allPageSelected(): boolean {
    if (this.rows.length === 0) return false;
    return this.rows.every((r) => this.selectedIds.has(r.id));
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get confirmHighlight(): string {
    return `${this.selectedCount} ใบ`;
  }

  get canSendSelected(): boolean {
    return this.canUpdate
      && this.taiwanSite
      && this.hasTaiwanUrl
      && this.selectedCount > 0
      && this.selectedCount <= this.maxSend
      && !this.isSending;
  }

  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal
        ? Number(userIdLocal)
        : Number(this.cookieService.get('user_id'));
    }
    if (this.selectedSiteId && this.userId) {
      this.loadTable();
    }
  }

  onRefresh(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  onPageSizeChange(size: number): void {
    this.pageSize = Number(size);
    this.currentPage = 1;
    this.loadTable();
  }

  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  toggleRow(id: number, checked: boolean): void {
    if (checked) {
      if (this.selectedIds.size >= this.maxSend && !this.selectedIds.has(id)) {
        this.toast.error(`เลือกได้สูงสุด ${this.maxSend} ใบต่อครั้ง`);
        return;
      }
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
  }

  toggleSelectAllPage(checked: boolean): void {
    if (!checked) {
      this.rows.forEach((r) => this.selectedIds.delete(r.id));
      return;
    }
    for (const r of this.rows) {
      if (this.selectedIds.size >= this.maxSend) {
        this.toast.error(`เลือกได้สูงสุด ${this.maxSend} ใบต่อครั้ง`);
        break;
      }
      this.selectedIds.add(r.id);
    }
  }

  clearSelection(): void {
    this.selectedIds.clear();
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.rows = [];
      this.totalItems = 0;
      return;
    }

    this.isLoading = true;
    this.api.getParkingCardsPendingTaiwanSyncForTable({
      siteId,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchText: this.searchText.trim() || undefined,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapPendingTaiwanSyncTable(res);
        this.rows = mapped.data;
        this.totalItems = mapped.total;
        this.totalPages = Math.max(1, mapped.totalPages || 1);
        this.taiwanSite = mapped.taiwanSite;
        this.hasTaiwanUrl = mapped.hasTaiwanUrl;
        this.lastFetchedAt = new Date();
        this.isLoading = false;
      },
      error: () => {
        this.rows = [];
        this.totalItems = 0;
        this.isLoading = false;
        this.toast.error('โหลดรายการไม่สำเร็จ');
      },
    });
  }

  onSendClick(): void {
    if (!this.canSendSelected) {
      if (!this.taiwanSite) this.toast.error('ลานนี้ไม่ได้เปิด taiwan_site');
      else if (!this.hasTaiwanUrl) this.toast.error('ลานนี้ยังไม่มี taiwan_url');
      else if (this.selectedCount === 0) this.toast.error('กรุณาเลือกบัตร');
      else if (this.selectedCount > this.maxSend) this.toast.error(`เลือกได้สูงสุด ${this.maxSend} ใบ`);
      return;
    }
    this.isConfirmOpen = true;
  }

  closeConfirm(): void {
    this.isConfirmOpen = false;
  }

  confirmSend(): void {
    this.isConfirmOpen = false;
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!this.userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    this.isSending = true;
    this.api.bulkSendParkingCardsToTaiwan({
      userId: this.userId,
      menuId,
      siteId: this.selectedSiteId,
      cardIds: Array.from(this.selectedIds),
    }).subscribe({
      next: (res) => {
        this.isSending = false;
        const raw = res as { isSuccess?: boolean; IsSuccess?: boolean; message?: string; Message?: string };
        const ok = !!(raw.isSuccess ?? raw.IsSuccess);
        const msg = raw.message || raw.Message || '';
        if (ok) {
          this.toast.success(msg || 'ส่ง Taiwan เรียบร้อย');
          this.selectedIds.clear();
          this.loadTable();
        } else {
          this.toast.error(msg || 'ส่ง Taiwan ไม่สำเร็จ');
          this.loadTable();
        }
      },
      error: (err) => {
        this.isSending = false;
        this.toast.error(err?.error?.message || 'ส่ง Taiwan ไม่สำเร็จ');
      },
    });
  }
}
